"use client";

import { Drawer } from "@/components/Drawer";
import { Calendar, ForkKnife, Invoice, OlgaLogo } from "@/components/Icons";
import Idle from "@/components/Idle";
import { Loading } from "@/components/Loading";
import Banners from "@/containers/Banners";
import Cart from "@/containers/Cart";
import { CartShortcut } from "@/containers/CartShortcut";
import Command from "@/containers/Command";
import Configure from "@/containers/Configure/Configure";
import Evaluate from "@/containers/Evaluate";
import Highlights from "@/containers/Highlights";
import LanguageSwitcher from "@/containers/LanguageSwitcher";
import Menu from "@/containers/Menu";
import Mural from "@/containers/Mural";
import ProductDetails from "@/containers/Product/ProductDetails";
import { Brand } from "@/core/Brand";
import { Catalog, CatalogEvents } from "@/core/Catalog";

import { Store, StoreEvents } from "@/core/Store";
import useEventListener from "@/hooks/useEventListener";
import { useNetLogicTableConfiguration } from "@/hooks/useNetLogicTableConfiguration";
import useScheduledCatalog from "@/hooks/useScheduleCatalog";
import { OrderStorage } from "@/storage/OrderStorage";
import styles from "@/styles/page.module.scss";
import { BugsnagStart } from "@/tools/BugsnagStart";
import Bugsnag from "@bugsnag/js";
import * as Availability from "@olga-food/schemas/lib/classes/schemas/catalog/Availability";
import { Builder } from "@olga-food/schemas/lib/classes/schemas/catalog/Builder";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import { Alert } from "@/components/Alert";
import CommandEcletica from "@/containers/CommandEcletica";
import { NetLogicTableConfiguration } from "@/core/NetLogicTableConfiguration";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";
import classNames from "classnames/bind";
import { NetlogicCatalog } from "@/services/NetLogic/catalog/NetLogicCatalog";
import { AndroidWebviewBridge } from "@/tools/AndroidWebviewBridge";
import { IndexedDBStorage, Logger } from "@/tools/Logger";
import { EcleticaCatalog } from "@/core/EcleticaCatalog";
import { EcleticaTableConfiguration } from "@/core/EcleticaTableConfiguration";
import CommandColibri from "@/containers/CommandColibri";
import { useTableConfiguration } from "@/hooks/useTableConfiguration";
import { TableServices } from "@/core/Enum";
import CommandSaipos from "@/containers/CommandSaipos";
const css = classNames.bind(styles);

const ErrorBoundary = BugsnagStart();

const executeOnSchema = (item, callback) => {
  for (let field of [
    "items",
    "extra_ingredients",
    "edges",
    "doughs",
    "sizes",
  ]) {
    if (item[field] && item[field].length > 0) {
      for (let children of item[field]) {
        executeOnSchema(children, callback);
        callback(children, item, field);
      }
    }
  }
};

export default function Home() {
  const [available_banners, setAvailableBanners] = useState(
    Store.getDeviceBanners(),
  );

  const [externalId, isValid] = useTableConfiguration();
  const [configurations] = useNetLogicTableConfiguration();

  const [isConfigured, setIsConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [openConfiguration, setOpenConfiguration] = useState(false);
  const [catalog, setCatalog] = useState({ items: [] });
  const [openProductDetails, setOpenProductDetails] = useState(false);
  const [openBanners, setOpenBanners] = useState(false);
  const [openMural, setOpenMural] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openEvaluate, setOpenEvaluate] = useState(false);
  const [openCommand, setOpenCommand] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [cartItemIndex, setCartItemIndex] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [highlights, setHighlights] = useState([]);
  const [error, setError] = useState(null);

  const loadConfigurations = async () => {
    if (Brand.alias && Store.alias) {
      await Brand.install(Brand.alias);
      await Store.install(Store.alias);
    }
  };

  const loadBridge = () => {
    AndroidWebviewBridge.getInstance();
  };

  const rotateLog = async () => {
    const storage = new IndexedDBStorage();
    await storage.rotateLog();
  };

  useEffect(() => {
    loadBridge();
    loadConfigurations();
    rotateLog();
  }, []);

  const [appConfigurations] = useAppConfiguration();

  const loadCatalog = async () => {
    setLoading(true);

    try {
      await loadConfigurations();

      setAvailableBanners(Store.getDeviceBanners());

      if (appConfigurations?.tableService === "ECLETICA") {
        const catalogResponse = await Store.catalog();

        OrderStorage.updateCartCatalogService(catalogResponse.service);

        executeOnSchema(catalogResponse, (i) => {
          i.visible = true;
          i.available = true;
        });

        const catalog = catalogResponse;

        const availableCatalog = Catalog.updateCatalogAvailability(catalog);
        setCatalog(availableCatalog);

        loadHighlights(availableCatalog?.items);

        await EcleticaCatalog.install();
      } else if (appConfigurations?.tableService === "NETLOGIC") {
        const netlogicCatalog = new NetlogicCatalog();
        const catalog = await netlogicCatalog.syncronize();

        setCatalog(catalog);
        loadHighlights(catalog?.items);
      } else {
        const catalogResponse = await Store.catalog();

        OrderStorage.updateCartCatalogService(catalogResponse.service);

        executeOnSchema(catalogResponse, (i) => {
          i.visible = true;
          i.available = true;
        });

        const catalog = catalogResponse;

        const availableCatalog = Catalog.updateCatalogAvailability(catalog);
        setCatalog(availableCatalog);

        loadHighlights(availableCatalog?.items);
      }

      Logger.info(`Atualizou o cardápio do App`);
    } catch (err) {
      if (appConfigurations?.tableService !== "ECLETICA") {
        const configurations = NetLogicTableConfiguration.fromStorage();
        Bugsnag.leaveBreadcrumb(
          "Configurações da NetLogic",
          configurations.export(),
        );
      } else {
        const configurations = EcleticaTableConfiguration.fromStorage();
        Bugsnag.leaveBreadcrumb(
          "Configurações da Eclética",
          configurations.export(),
        );
      }

      Bugsnag.notify(err);

      Logger.error(
        `Falha na atualização do cardápio durante o carregamento do App`,
        err,
      );

      setError({
        title: "Não foi possível carregar o cardápio",
      });
    }
  };

  const onCatalogUpdate = async (event) => {
    Logger.info("Evento interno de atualização do cardápio disparado");

    try {
      if (Store.alias) {
        const catalog = await Catalog.getMemorizedCatalog(Store.alias);

        setCatalog(catalog);
        loadHighlights(catalog?.items);

        Logger.info(`Atualizou o cardápio do App`);
      }
    } catch (err) {
      Logger.error(
        `Falha na atualização do cardápio do App através do evento`,
        err,
      );
    }
  };

  const updateCatalogAvailability = () => {
    setCatalog((currentCatalog) => {
      return Catalog.updateCatalogAvailability(currentCatalog);
    });
  };

  useEffect(() => {
    const availabityTimer = setInterval(async () => {
      updateCatalogAvailability();
    }, 1000 * 10);

    return () => clearInterval(availabityTimer);
  }, []);

  const loadHighlights = (catalog) => {
    const extractProductsCategorys = catalog.filter(
      (category) => category.visible == true && category.available == true,
    );
    const res = extractProductsCategorys.map((item) => item.items);
    const highlights = res
      .flat()
      .filter(
        (item) =>
          Availability.isAvailable(item, null, -3) &&
          item.highlight &&
          item.show &&
          item.showAll !== false &&
          item.showTablet !== false &&
          item.visible,
      );

    setHighlights(highlights);
    setLoading(false);
  };

  //Evento de atualização do cardapio
  useEventListener(StoreEvents.STORE_CHANGED, () => {
    // setLoading(true);
    if (Store.alias) loadCatalog();
  });

  useEventListener(CatalogEvents.CATALOG_UPDATED, onCatalogUpdate);

  useScheduledCatalog(loadCatalog);

  useEffect(() => {
    if (!Brand.alias || !Store.alias) {
      setIsConfigured(false);
      setLoading(false);
      return;
    }

    if (!isValid) {
      setIsConfigured(false);
      setLoading(false);
      return;
    }

    setIsConfigured(true);
    setLoading(false);
  }, [isValid]);

  useEffect(() => {
    if (isConfigured) {
      setOpenBanners(
        available_banners.length > 0 &&
          !openMural &&
          !openMenu &&
          !openCommand &&
          !openCart,
      );
      if (Store.alias) loadCatalog();
    }

    const updateBanners = () => {
      const banners = Store.getDeviceBanners();
      setAvailableBanners(banners);
    };

    const interval_id = setInterval(updateBanners, 60000);
    return () => {
      clearInterval(interval_id);
    };
  }, [isConfigured]);

  const deepCopy = (data) => {
    if (!data) {
      return data;
    }

    return JSON.parse(JSON.stringify(data));
  };

  const mountPizza = (category, product) => {
    let localSelectedProduct = product;

    const firstSize = product.sizes[0];
    const officialProductSizes = product.sizes.map((s) => s.ref_id);

    const pizzaProduct = Builder.factory("Pizza");
    pizzaProduct.id = product.id;
    pizzaProduct.name = product.name;
    pizzaProduct.max_flavors = firstSize.max;
    pizzaProduct.highlight = product.highlight;
    pizzaProduct.sizes = product.sizes.map((size) => {
      return {
        ...size,
        selected: size.ref_id === firstSize.ref_id,
      };
    });

    pizzaProduct.doughs = category.doughs.filter((dough) => {
      dough.quantity = 0;
      return (
        dough.sizes.filter((s) => officialProductSizes.indexOf(s.ref_id) > -1)
          .length > 0
      );
    });

    pizzaProduct.edges = category.edges.filter((edge) => {
      edge.quantity = 0;
      return (
        edge.sizes.filter((s) => officialProductSizes.indexOf(s.ref_id) > -1)
          .length > 0
      );
    });

    pizzaProduct.items = category.items.filter((item) => {
      item.quantity = 0;

      if (product.type == "flavor" && product.ref_id == item.ref_id) {
        item.quantity = 1;
      }

      return (
        item.sizes.filter((s) => officialProductSizes.indexOf(s.ref_id) > -1)
          .length > 0
      );
    });

    pizzaProduct.extra_ingredients = category.extra_ingredients.filter(
      (extra) => {
        return (
          extra.sizes.filter((size) =>
            officialProductSizes.includes(size.ref_id),
          ).length > 0
        );
      },
    );

    localSelectedProduct = Builder.mountSchema(
      JSON.parse(JSON.stringify(pizzaProduct)),
    );

    localSelectedProduct.quantity = localSelectedProduct.quantity || 1;

    localSelectedProduct.image = product.image;
    localSelectedProduct.description = product.description;

    return localSelectedProduct;
  };

  const onSelectProduct = (product) => {
    let category = null;
    for (const item of catalog.items) {
      if (item.id == product.parent) {
        category = item;

        if (item.type != "flavor") {
          product.extra_ingredients = deepCopy([...item.extra_ingredients]);
        }

        break;
      }
    }

    if (product.type === "flavor") {
      const mountedPizza = mountPizza(deepCopy(category), deepCopy(product));
      setSelectedProduct({
        category: deepCopy(category),
        product: mountedPizza,
      });
    } else {
      setSelectedProduct(
        deepCopy({
          category,
          product,
        }),
      );
    }

    setOpenProductDetails(true);
    setSelectedProduct(JSON.parse(JSON.stringify(product)));
    setCartItemIndex(null);
  };

  const intervalRef = useRef(null);

  const startPressedCounter = () => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      stopPressedCounter();
      setOpenConfiguration(true);
    }, 1000);
  };

  const stopPressedCounter = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const t = useTranslations();

  if ((!isConfigured && !loading) || openConfiguration) {
    return (
      <Configure
        onSuccess={() => {
          setIsConfigured(true);
          setOpenConfiguration(false);
          setError(null);
        }}
        onBack={openConfiguration ? () => setOpenConfiguration(false) : null}
        onlyDailyConfiguration={
          openConfiguration === true &&
          appConfigurations?.tableService !== "ECLETICA"
        }
      />
    );
  }

  return (
    <ErrorBoundary>
      <main className={styles.main}>
        <div
          className={css({ loadingContainer: true, show: loading })}
          onMouseDown={startPressedCounter}
          onTouchStart={startPressedCounter}
          onTouchEnd={stopPressedCounter}
          onMouseUp={stopPressedCounter}
          onMouseLeave={stopPressedCounter}
        >
          <Image
            src="https://olga.tech/favicon-olga.png"
            width={100}
            height={100}
            alt="Olga"
          />
          <Loading className="mt-20" />
        </div>

        <div className={styles.sidebar}>
          <p className={styles.table}>
            {t("table")} {externalId}
            {configurations.orderEmployee && (
              <>
                &nbsp;-&nbsp;<span>Garçom {configurations.orderEmployee}</span>
              </>
            )}
          </p>
          <div
            className={styles.logo}
            onMouseDown={startPressedCounter}
            onTouchStart={startPressedCounter}
            onTouchEnd={stopPressedCounter}
            onMouseUp={stopPressedCounter}
            onMouseLeave={stopPressedCounter}
          >
            {Brand.avatar && (
              <Image
                src={Brand.avatar}
                alt={Brand.name || ""}
                width={95}
                height={95}
              />
            )}
          </div>
          <nav className={styles.menu}>
            <ul className={styles.items}>
              <li className={styles.item} onClick={() => setOpenMenu(true)}>
                <ForkKnife />
                <p>{t("menu")}</p>
              </li>
              {/* <li className={styles.item} onClick={() => setOpenEvaluate(true)}>
                <Star />
                <p>{t("rateThePlace")}</p>
              </li> */}
              <li className={styles.item} onClick={() => setOpenMural(true)}>
                <Calendar />
                <p>{t("houseBoard")}</p>
              </li>
              <li
                className={styles.item}
                onClick={() => {
                  setOpenCommand(true);
                }}
              >
                <Invoice />
                <p>{t("myAccount")}</p>
              </li>
            </ul>
          </nav>

          <LanguageSwitcher />

          <div className={styles.olga}>
            <div className="d-flex flex-col align-center">
              <OlgaLogo />
            </div>
          </div>
        </div>

        <Highlights
          products={highlights}
          onSelectProduct={(product) =>
            onSelectProduct && onSelectProduct(product)
          }
        />

        <div className={styles.cart}>
          <CartShortcut onOpenCart={() => setOpenCart(true)} />
        </div>

        {error && (
          <Alert
            title={error.title}
            subtitle={error.message}
            open={!!error}
            onClose={() => setError(null)}
          />
        )}

        <Drawer
          open={openBanners && available_banners.length > 0}
          onClose={() => {}}
          withoutPadding
        >
          <Banners
            banners={available_banners}
            onClose={() => setOpenBanners(false)}
            startPressedCounter={startPressedCounter}
            stopPressedCounter={stopPressedCounter}
          />
        </Drawer>

        <Drawer
          open={openMenu}
          onClose={() => setOpenMenu(false)}
          withoutPadding
        >
          <Menu
            catalog={catalog}
            onClose={() => setOpenProductDetails(false)}
            onBack={() => setOpenMenu(false)}
            onOpenCart={() => setOpenCart(true)}
          />
        </Drawer>

        <Drawer
          open={openCommand}
          onClose={() => setOpenCommand(false)}
          withoutPadding
        >
          {appConfigurations?.tableService === TableServices.NETLOGIC && (
            <Command onClose={() => setOpenCommand(false)} />
          )}

          {appConfigurations?.tableService === TableServices.ECLETICA && (
            <CommandEcletica onClose={() => setOpenCommand(false)} />
          )}

          {appConfigurations?.tableService === TableServices.COLIBRI && (
            <CommandColibri onClose={() => setOpenCommand(false)} />
          )}

          {appConfigurations?.tableService === TableServices.SAIPOS && (
            <CommandSaipos onClose={() => setOpenCommand(false)} />
          )}
        </Drawer>

        <Drawer
          open={openEvaluate}
          onClose={() => setOpenEvaluate(false)}
          withoutPadding
        >
          <Evaluate onClose={() => setOpenEvaluate(false)} />
        </Drawer>

        <Drawer
          open={openMural}
          onClose={() => setOpenMural(false)}
          withoutPadding
          direction="center-full"
        >
          <Mural
            products={highlights}
            onSelectProduct={(product) =>
              onSelectProduct && onSelectProduct(product)
            }
            onClose={() => setOpenMural(false)}
          />
        </Drawer>

        <Drawer
          open={openProductDetails}
          direction="center-full"
          onClose={() => setOpenProductDetails(false)}
          withoutPadding
        >
          <ProductDetails
            cartItemIndex={cartItemIndex}
            product={selectedProduct}
            onClose={() => setOpenProductDetails(false)}
          />
        </Drawer>

        {isConfigured && (
          <Idle
            onOpenMural={() => {
              if (available_banners.length > 0) setOpenBanners(true);
              OrderStorage.clearCartItems();
              setOpenMural(false);
              setOpenMenu(false);
              setOpenCart(false);
              setOpenEvaluate(false);
              setOpenCommand(false);
              setOpenProductDetails(false);
            }}
            timeout={3 * 60 * 1000}
          />
        )}

        <Cart
          open={openCart}
          onClose={() => setOpenCart(false)}
          withoutPadding
        />
      </main>
    </ErrorBoundary>
  );
}
